import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader } from "../components/ui/card";
import { toast } from "sonner";
import { Loader2, QrCode, X, Camera, Keyboard } from "lucide-react";
import QrScanner from "../components/QrScanner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_75f76eb1-ed0f-4a2e-8af9-a573c953ed2b/artifacts/jcb4nruu_7d3b96f2-f08e-453b-8c2c-591199068bb4.jpeg";

export default function Login() {
  const [userCode, setUserCode] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [qrMode, setQrMode] = useState(false);
  const [qrManual, setQrManual] = useState(false);
  const [qrInput, setQrInput] = useState("");
  const qrInputRef = useRef(null);
  const submittingRef = useRef(false);

  const { login, loginWithQr } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (qrMode && qrManual) qrInputRef.current?.focus();
  }, [qrMode, qrManual]);

  const submitDecoded = async (decoded) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true);
    try {
      const result = await loginWithQr(decoded.trim());
      if (result.success) {
        toast.success("QR válido - a entrar...");
        setTimeout(() => navigate("/", { replace: true }), 100);
      } else {
        toast.error(result.error || "QR inválido");
        submittingRef.current = false;
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!userCode || !pin) {
      toast.error("Preencha todos os campos");
      return;
    }
    setLoading(true);
    const result = await login(userCode, pin);
    setLoading(false);
    if (result.success) {
      setTimeout(() => navigate("/", { replace: true }), 100);
    } else {
      toast.error(result.error);
    }
  };

  const handleQrSubmit = async (e) => {
    e.preventDefault();
    if (!qrInput || qrInput.length < 8) return;
    setLoading(true);
    const result = await loginWithQr(qrInput.trim());
    setLoading(false);
    if (result.success) {
      setQrInput("");
      setTimeout(() => navigate("/", { replace: true }), 100);
    } else {
      toast.error(result.error);
      setQrInput("");
      qrInputRef.current?.focus();
    }
  };

  const handlePinInput = (value, setter) => {
    const numericValue = value.replace(/\D/g, "").slice(0, 6);
    setter(numericValue);
  };

  const closeQrMode = () => {
    setQrMode(false);
    setQrManual(false);
    setQrInput("");
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-100 to-slate-200">
      <Card className="w-full max-w-md border-border shadow-xl" data-testid="login-card">
        <CardHeader className="text-center pb-6">
          <div className="mx-auto mb-4">
            <img src={LOGO_URL} alt="Logo" className="h-24 w-auto object-contain mx-auto" />
          </div>
          <CardDescription className="text-base text-muted-foreground">
            Sistema de Gestão de Produção
          </CardDescription>
        </CardHeader>

        <CardContent>
          {qrMode ? (
            <div className="space-y-4" data-testid="qr-login-form">
              {!qrManual ? (
                <>
                  <div className="text-center py-1">
                    <p className="text-sm text-muted-foreground">
                      Aponte o crachá para a câmara — o login será automático.
                    </p>
                  </div>
                  <QrScanner
                    onDecode={submitDecoded}
                    size={280}
                    borderColor="#2563eb"
                    scannerId="login-qr-scanner"
                    testId="login-qr"
                  />
                  {loading && (
                    <div className="flex items-center justify-center gap-2 text-sm text-blue-700">
                      <Loader2 className="h-4 w-4 animate-spin" /> A validar QR...
                    </div>
                  )}
                  <Button type="button" variant="outline" className="w-full" onClick={() => setQrManual(true)} data-testid="qr-switch-manual">
                    <Keyboard className="h-4 w-4 mr-2" /> Introduzir código manualmente
                  </Button>
                </>
              ) : (
                <form onSubmit={handleQrSubmit} className="space-y-4">
                  <div className="text-center py-2">
                    <QrCode className="h-12 w-12 mx-auto text-blue-600" />
                    <p className="text-sm text-muted-foreground mt-2">Cole ou digite o código QR do crachá</p>
                  </div>
                  <Input
                    ref={qrInputRef}
                    type="text"
                    placeholder="Código QR..."
                    value={qrInput}
                    onChange={(e) => setQrInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") handleQrSubmit(e); }}
                    className="h-12 text-center font-mono"
                    data-testid="qr-input"
                    autoFocus
                  />
                  <Button type="submit" className="w-full h-12" disabled={loading || !qrInput} data-testid="qr-submit">
                    {loading ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <QrCode className="mr-2 h-5 w-5" />}
                    Validar QR
                  </Button>
                  <Button type="button" variant="ghost" className="w-full text-sm" onClick={() => setQrManual(false)} data-testid="qr-switch-camera">
                    <Camera className="h-4 w-4 mr-1" /> Usar câmara
                  </Button>
                </form>
              )}
              <Button type="button" variant="ghost" className="w-full text-sm" onClick={closeQrMode} data-testid="qr-back">
                <X className="h-4 w-4 mr-1" /> Voltar ao login por PIN
              </Button>
            </div>
          ) : (
            <form onSubmit={handleLogin} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="userCode" className="text-foreground">Código de Utilizador</Label>
                <Input
                  id="userCode"
                  type="text"
                  placeholder="Ex: OP001"
                  value={userCode}
                  onChange={(e) => setUserCode(e.target.value.toUpperCase())}
                  className="h-12 text-lg font-mono bg-white"
                  data-testid="login-user-code"
                  autoComplete="username"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="pin" className="text-foreground">PIN (6 dígitos)</Label>
                <Input
                  id="pin"
                  type="password"
                  placeholder="••••••"
                  value={pin}
                  onChange={(e) => handlePinInput(e.target.value, setPin)}
                  className="h-12 text-lg font-mono tracking-widest bg-white"
                  maxLength={6}
                  data-testid="login-pin"
                  autoComplete="current-password"
                />
              </div>

              <Button type="submit" className="w-full h-14 text-lg font-bold" disabled={loading} data-testid="login-submit">
                {loading ? (<><Loader2 className="mr-2 h-5 w-5 animate-spin" />A entrar...</>) : ("Entrar")}
              </Button>

              <div className="text-center">
                <p className="text-xs text-muted-foreground" data-testid="login-only-admin-can-create">
                  Apenas administradores podem criar utilizadores. Para nova conta, peca um link de convite ao seu administrador.
                </p>
              </div>
              <Button type="button" variant="outline" className="w-full h-11 border-blue-300 text-blue-700 hover:bg-blue-50" onClick={() => setQrMode(true)} data-testid="switch-to-qr">
                <Camera className="h-4 w-4 mr-2" /> Entrar com QR (câmara)
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
